import React from "react"
import { useTranslation } from "react-i18next"
import { Link, NavLink, useLocation } from "react-router"

import { Page404 } from "@/components/nav/page404"
import { NzDataGrid } from "@/components/nzDataGrid"
import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId } from "@/contexts"
import { AdminEntityFieldsSkeleton } from "@/modules/admin/components/admin-entity-fields-skeleton"
import {
  createAdminGridViewAction,
  useAdminGridActionsColumn,
} from "@/modules/admin/components/admin-grid-actions-column"
import {
  MeterReadonlyFields,
  useAdminMeterPageState,
} from "@/modules/admin/meters"
import { DataGridToolbar } from "@/nzds/data-grid"
import { billToGridRecord } from "@/services/bill/bill-grid-utils"
import type { BillGridRecord } from "@/services/bill/bill.types"
import { useBillsInfinite } from "@/services/bill/use-bills-infinite"
import { useFlags } from "@/services/feature/use-flags"
import { useCommonBillColumns } from "@/utils/bill/use-common-bill-columns"
import { useUrl } from "@/utils/url"

import { Box, Button, Link as MuiLink, Stack, Typography } from "@mui/material"
import type { GridColDef, GridSlotProps, GridSlots } from "@mui/x-data-grid-pro"

export const AdminMeterRoute: React.FC = () => {
  const { t } = useTranslation("common")
  const location = useLocation()
  const { activeOrganizationId } = useActiveOrganizationId()
  const { admin_bills: isAdminBillsEnabled } = useFlags()
  const { adminBillPath, adminMeterEditPath, adminMetersPath } = useUrl()
  const { meterData, meterId, meterIsLoading, meterIsUnavailable } =
    useAdminMeterPageState()

  const organizationId = activeOrganizationId ?? ""

  const {
    billsData,
    billsFetchNextPage,
    billsHasNextPage,
    billsIsFetching,
    billsIsFetchingNextPage,
    billsIsLoading,
    billsIsPlaceholderData,
  } = useBillsInfinite({
    apiQueryOptions: {
      is_current: true,
      limit: 50,
      meter_id: meterId,
      sort: "start_date:desc",
    },
    organizationId,
    queryOptions: {
      enabled:
        isAdminBillsEnabled &&
        Boolean(organizationId) &&
        meterId !== undefined &&
        meterData != null,
    },
  })

  const billRows: BillGridRecord[] = React.useMemo(
    () =>
      (billsData?.pages.flatMap((page) => page.items) ?? []).map((bill) =>
        billToGridRecord(bill)
      ),
    [billsData]
  )

  const commonBillColumns = useCommonBillColumns()
  const billActionsColumn = useAdminGridActionsColumn<BillGridRecord>({
    getActions: ({ id }) => [
      createAdminGridViewAction(adminBillPath(String(id)), t("button.view")),
    ],
  })
  const billsGridColumns = React.useMemo<GridColDef<BillGridRecord>[]>(
    () => [
      commonBillColumns.periodStart,
      commonBillColumns.periodEnd,
      commonBillColumns.usage,
      commonBillColumns.cost,
      commonBillColumns.flowDirection,
      billActionsColumn,
    ],
    [billActionsColumn, commonBillColumns]
  )
  if (!organizationId) {
    return null
  }

  if (meterIsUnavailable) {
    return <Page404 />
  }

  const isBillsGridLoading =
    billsIsLoading ||
    billsIsFetchingNextPage ||
    (billsIsFetching && billRows.length > 0)

  return (
    <Stack sx={{ gap: 2 }}>
      <PageTitle
        breadcrumbActions={
          <MuiLink component={Link} to={`${adminMetersPath}${location.search}`}>
            {t("admin.meters")}
          </MuiLink>
        }
        heading={t("admin.meterDetail.title")}
        primaryActions={
          <Button
            component={NavLink}
            disabled={meterIsLoading}
            to={`${adminMeterEditPath(String(meterId))}${location.search}`}
            variant="contained"
          >
            {t("button.edit")}
          </Button>
        }
      />
      <PageCard>
        <Stack sx={{ gap: 2 }}>
          {meterIsLoading || !meterData ? (
            <AdminEntityFieldsSkeleton />
          ) : (
            <MeterReadonlyFields meter={meterData} />
          )}
          {isAdminBillsEnabled ? (
            <>
              <Box sx={{ pt: 1 }}>
                <Typography component="h2" variant="subtitle1">
                  {t("editMeter.billsLabel")}
                </Typography>
              </Box>
              <Box sx={{ height: 320 }}>
                <NzDataGrid
                  aria-label={t("editMeter.billsGridAriaLabel")}
                  columns={billsGridColumns}
                  disableColumnFilter
                  disableColumnMenu
                  disableColumnSorting
                  getRowId={(row: BillGridRecord) => row.id}
                  loading={meterIsLoading || isBillsGridLoading}
                  initialState={{ pinnedColumns: { right: ["actions"] } }}
                  onRowsScrollEnd={() => {
                    if (billsHasNextPage && !billsIsFetchingNextPage) {
                      billsFetchNextPage().catch(() => undefined)
                    }
                  }}
                  rows={billRows}
                  slotProps={{
                    toolbar: {
                      isRecordCountLoading:
                        billsIsFetching || billsIsPlaceholderData,
                      recordCount: billsData?.pages[0]?.total,
                    } as GridSlotProps["toolbar"],
                  }}
                  slots={{
                    toolbar: DataGridToolbar as GridSlots["toolbar"],
                  }}
                />
              </Box>
            </>
          ) : null}
        </Stack>
      </PageCard>
    </Stack>
  )
}
